import React from 'react';
import { Box, ButtonV2, Flex, TextV2, useToast } from '@withjoy/joykit';
import { JoyAiV2 } from '@withjoy/joykit/icons';
import { pxToRem } from '@shared/joykit/packages/core/common/themes/shared/typography';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUserConsent } from '@shared/utils/userConsent';
import { AiBorderBox } from '@shared/components/AiBorderBox/AiBorderBox';
import { AiGradientText } from '@shared/components/AiGradientText';
import { useAdminStayAreasTelemetry } from './StayAreas.telemetry';

const STAY_AREAS_EMPTY_STATE_IMAGE = 'https://withjoy.com/assets/public/travel/stay-areas-empty-state-v2.png';

type StayAreasEmptyStateProps = Readonly<{
  onShowSuggestedStayAreas: () => void;
}>;

export const StayAreasEmptyState: React.FC<StayAreasEmptyStateProps> = ({ onShowSuggestedStayAreas }) => {
  const { tq } = useTranslation('stayAreasAdmin');
  const telemetry = useAdminStayAreasTelemetry();
  const { toast } = useToast();
  const { getUserConsent, setUserConsent } = useUserConsent();
  const hasSubmittedFeedback = getUserConsent('hasSubmittedStayAreasFeedback') === true;

  const handleFeedback = useEventCallback((feedback: 0 | 1) => {
    telemetry.stayAreaFeedback({ feedback });
    setUserConsent('hasSubmittedStayAreasFeedback', true);
    toast(tq`Thanks for your feedback!`);
  });

  const handleShowSuggestedStayAreas = useEventCallback(() => {
    telemetry.showSuggestedStayAreasClicked({ source: 'emptyState' });
    onShowSuggestedStayAreas();
  });

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      textAlign="center"
      paddingX={{ _: 6, sm2: 9 }}
      paddingY={4}
      height={'100%'}
      flexGrow={1}
    >
      <Box
        as={'img'}
        alt={tq`Neighborhoods map`}
        src={STAY_AREAS_EMPTY_STATE_IMAGE}
        width={'100%'}
      />
      <Box marginTop={5}>
        <TextV2 typographyVariant={'hed4'}>{tq`Neighborhood Suggestions`}</TextV2>
        <TextV2
          typographyVariant={'body1'}
          color={'mono10'}
          marginTop={4}
        >
          {tq`Help your guests discover the best areas for vacation homes near your venue.`}
        </TextV2>
      </Box>
      <Box
        marginTop={5}
        width="fit-content"
        cursor="pointer"
        onClick={handleShowSuggestedStayAreas}
      >
        <AiBorderBox
          width="fit-content"
          borderRadius={32}
          padding={`${pxToRem(12)} ${pxToRem(20)}`}
          borderWidth={1}
        >
          <JoyAiV2 size={24} />
          <AiGradientText>{tq`Suggest Neighborhoods`}</AiGradientText>
        </AiBorderBox>
      </Box>
      {!hasSubmittedFeedback && (
        <Flex
          flexDirection="column"
          alignItems="center"
          marginTop={7}
          gap={4}
        >
          <TextV2
            typographyVariant={'body1'}
            color={'mono10'}
            maxWidth={pxToRem(200)}
          >{tq`Ability to add and edit coming soon. Interested?`}</TextV2>
          <Flex
            gap={4}
            justifyContent="center"
          >
            <ButtonV2
              variant="outline"
              intent="neutral"
              shape="rounded"
              padding={0}
              borderRadius={pxToRem(32)}
              width={pxToRem(52)}
              minWidth={pxToRem(52)}
              height={pxToRem(40)}
              borderColor="mono3"
              border="1px solid"
              onClick={() => handleFeedback(0)}
              aria-label={tq`Not helpful`}
            >
              <span aria-hidden="true">👎</span>
            </ButtonV2>
            <ButtonV2
              variant="outline"
              intent="neutral"
              shape="rounded"
              padding={0}
              borderRadius={pxToRem(32)}
              width={pxToRem(52)}
              minWidth={pxToRem(52)}
              height={pxToRem(40)}
              border="1px solid"
              borderColor="mono3"
              onClick={() => handleFeedback(1)}
              aria-label={tq`Helpful`}
            >
              <span aria-hidden="true">👍</span>
            </ButtonV2>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
